// The Systems & infrastructure lens of the analysis screen: each node is a system's run of consecutive actions, from
// when information reaches it to when it passes to another system, and the edges are those interfaces: automated when
// the receiving system picks the information up with no one involved, manual when people move it. Work done outside
// any system makes runs of its own, so the graph shows where information leaves systems and has to be keyed back in.
// Built from the analysis graph, so it can be tested without a page
import { AnalysisEdge, AnalysisNode } from '../common/analysis-model';
import { AUTOMATION_LABELS, AutomationLevel, automationLevel, systemsOf } from '../systems/systems-model';
import type { LensGraphEdge, LensGraphNode } from './lens-node-style';
import { actionCount, buildRuns, Handover, LensOutput, LensRun, outputsGraph, stepsFact } from './runs';

// how a run's work is done: all by systems, all by people in systems, a mix of the two, or outside systems
export type RunAutomation = AutomationLevel | 'mixed';

export const RUN_AUTOMATION_LABELS: Record<RunAutomation, string> = { ...AUTOMATION_LABELS, mixed: 'Partly automated' };

export interface SystemRun extends LensRun {
    systemKeys: string[];               // none for work outside systems
    automation: RunAutomation;
    automatedShare: number;             // of its actions, done by systems alone
    aiAgent: boolean;                   // an AI agent is one of its systems
    roleNames: string[];                // the people's roles working in it
    cost: number;                       // a year, of its actions
    carbon: number;                     // kg CO2e a year, of its actions
    actionLevels: AutomationLevel[];    // each action's, in order
}

export type InterfaceKind = 'automated' | 'manual' | 'mixed';

export interface SystemHandover extends Handover {
    automatedItems: number;             // of its items, picked up with no one involved
    kind: InterfaceKind;
}

export interface SystemLens {
    runs: SystemRun[];
    handovers: SystemHandover[];
    outputs: LensOutput[];
    outputLinks: Handover[];            // from the runs that create them
}

const performer = (a: AnalysisNode) => {
    const systems = systemsOf(a);
    return systems.length ? `system:${systems.join('+')}` : (a.roles || []).length ? 'outside' : 'unassigned';
};

// external: the roles that are external organisations, whose steps are theirs rather than staff's
export const buildSystemsLens = (analysis: { nodes: AnalysisNode[]; edges: AnalysisEdge[] }, names: Map<string, string>, aiAgents: Set<string>,
    modelKeys?: Set<string>, external: Set<string> = new Set()): SystemLens => {
    const { runs, handovers, actions, outputs, outputLinks } = buildRuns(analysis, performer, modelKeys);
    const systemRuns: SystemRun[] = runs.map(run => {
        const systemKeys = systemsOf(run.actions[0]);
        const levels = run.actions.map(a => automationLevel(a, external));
        const automated = levels.filter(l => l === 'automated').length;
        const automation: RunAutomation = !systemKeys.length ? levels[0] : automated === levels.length ? 'automated' : automated ? 'mixed' : 'assisted';
        const roleKeys = [...new Set(run.actions.flatMap(a => (a.roles || []).map(r => r._key)))];
        return {
            key: run.key, systemKeys, automation, automatedShare: levels.length ? automated / levels.length : 0, aiAgent: systemKeys.some(s => aiAgents.has(s)),
            name: systemKeys.length ? systemKeys.map(s => names.get(s) || 'Unnamed system').join(' & ') : automation === 'manual' ? 'Outside systems' : 'No one assigned',
            actionKeys: run.actions.map(a => a._key), actionNames: run.actions.map(a => a.name || 'Unnamed action'), actionLevels: levels,
            hours: run.hours, segments: run.segments, roleNames: roleKeys.map(k => names.get(k) || 'Unnamed role'),
            cost: run.actions.reduce((sum, a) => sum + (a.cost || 0), 0), carbon: run.actions.reduce((sum, a) => sum + (a.carbon || 0), 0),
        };
    });
    // an interface is automated where the receiving action has no one doing it
    const items = new Map(analysis.edges.map(e => [`${e.from}>${e.to}`, e.items || 0]));
    const systemHandovers: SystemHandover[] = handovers.map(h => {
        const automatedItems = h.actionPairs.filter(([, to]) => automationLevel(actions.get(to)!, external) === 'automated').reduce((sum, [from, to]) => sum + (items.get(`${from}>${to}`) || 0), 0);
        const automatedPairs = h.actionPairs.filter(([, to]) => automationLevel(actions.get(to)!, external) === 'automated').length;
        const kind: InterfaceKind = automatedPairs === h.actionPairs.length ? 'automated' : automatedPairs ? 'mixed' : 'manual';
        return { ...h, automatedItems, kind };
    });
    return { runs: systemRuns, handovers: systemHandovers, outputs, outputLinks };
};

// how the cards are filled: by how automated their work is, by its cost or carbon, or not at all
export type SystemsColourBy = 'automation' | 'cost' | 'carbon' | 'none';

// light tints of the automation colours, so a card's text stays readable; the dot keeps the full colour
export const AUTOMATION_COLOURS: Record<RunAutomation, string> = {
    automated: '#0f8b8d', mixed: '#2a78d6', assisted: '#fab219', manual: '#e34948', external: '#8e6fc1', unassigned: '#a9a8a3',
};
const AUTOMATION_TINTS: Partial<Record<RunAutomation, string>> = { automated: '#dcf1f1', mixed: '#e0ecfb', assisted: '#fef3d6', manual: '#fde8e8' };
export const INTERFACE_COLOURS: Record<InterfaceKind, string> = { automated: '#0f8b8d', mixed: '#64748B', manual: '#c2410c' };
export const INTERFACE_LABELS: Record<InterfaceKind, string> = { automated: 'Automated interface', mixed: 'Partly automated', manual: 'People move it' };
export const SYSTEMS_RAMP = ['#cde2fb', '#9ec5f4', '#6da7ec', '#3987e5', '#256abf', '#184f95', '#0d366b'];

export const systemsLensGraph = (lens: SystemLens, modelColours: Map<string, string>, colourBy: SystemsColourBy,
    format: { hours: (v: number) => string; count: (v: number) => string; money: (v: number) => string }): { nodes: LensGraphNode[]; edges: LensGraphEdge[] } => {
    const measure = (run: SystemRun) => colourBy === 'cost' ? run.cost : colourBy === 'carbon' ? run.carbon : 0;
    const max = Math.max(0, ...lens.runs.map(measure));
    const fill = (run: SystemRun) => {
        if (colourBy === 'automation') return AUTOMATION_TINTS[run.automation];
        // by the square root of the share of the most, so one very large run doesn't leave the rest all the lightest shade
        if ((colourBy === 'cost' || colourBy === 'carbon') && max > 0) return SYSTEMS_RAMP[Math.min(SYSTEMS_RAMP.length - 1, Math.floor(Math.sqrt(measure(run) / max) * SYSTEMS_RAMP.length))];
        return undefined;
    };
    const names = new Map(lens.runs.map(r => [r.key, r.name]));
    const nodes = lens.runs.map(run => {
        const outside = !run.systemKeys.length;
        const detail = colourBy === 'cost' ? `${actionCount(run.actionKeys.length)} · ${format.money(run.cost)} a year`
            : colourBy === 'carbon' ? `${actionCount(run.actionKeys.length)} · ${format.count(run.carbon)} kg CO2e`
                : outside || run.automation === 'automated' ? actionCount(run.actionKeys.length) + (run.hours ? ` · ${format.hours(run.hours)} h a year` : '')
                    : `${actionCount(run.actionKeys.length)} · ${Math.round(run.automatedShare * 100)}% automated`;
        return {
            key: run.key, label: run.aiAgent ? `${run.name} (AI)` : run.name, detail, dashed: outside, status: run.automation, fill: fill(run),
            segments: run.segments.map(s => ({ colour: modelColours.get(s.modelKey) || '#a9a8a3', share: s.share })),
            tooltip: `${run.name}: ${run.actionNames.join(' → ')}. ${RUN_AUTOMATION_LABELS[run.automation]}${run.roleNames.length ? `, by ${run.roleNames.join(', ')}` : ''}`,
            tip: {
                kind: outside ? 'Work outside systems' : run.aiAgent ? 'AI agent' : 'System',
                title: run.name,
                facts: [
                    { label: 'Steps', value: stepsFact(run.actionNames) },
                    { label: 'How', value: RUN_AUTOMATION_LABELS[run.automation] },
                    ...(run.roleNames.length ? [{ label: 'Roles', value: run.roleNames.join(', ') }] : []),
                ],
                figures: [
                    { label: 'Hours', value: format.hours(run.hours), note: 'a year, in these steps' },
                    { label: 'Actions', value: String(run.actionKeys.length), note: outside || run.automation === 'automated' ? undefined : `${Math.round(run.automatedShare * 100)}% automated` },
                    ...(run.cost ? [{ label: 'Cost', value: format.money(run.cost), note: 'a year' }] : []),
                    ...(run.carbon ? [{ label: 'Carbon', value: format.count(run.carbon), note: 'kg CO2e a year' }] : []),
                    ...(run.segments.length > 1 ? [{ label: 'Models', value: String(run.segments.length) }] : []),
                ],
            },
        };
    });
    const edges = lens.handovers.map(h => ({
        key: h.key, from: h.from, to: h.to, weight: h.items, colour: INTERFACE_COLOURS[h.kind], dashed: h.kind === 'manual',
        tooltip: `${names.get(h.from)} → ${names.get(h.to)}: ${format.count(h.items)} items a year. ${h.kind === 'automated' ? 'An automated interface'
            : h.kind === 'manual' ? 'People move the information' : `${format.count(h.automatedItems)} of them automated, the rest moved by people`}`,
    }));
    const outputs = outputsGraph(lens.outputs, lens.outputLinks, names, modelColours, format.count);
    return { nodes: [...nodes, ...outputs.nodes], edges: [...edges, ...outputs.edges] };
};
